import React, { useEffect, useState } from "react";
import { Menu, UserCircle, LogOut, Building2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { removeAll } from "../../hooks/hooks.localStorage";
import { handleGet, handleSetActiveYear, handleUpdateProfilPicture } from "../../controllers/controller.all";
import { getImageUrl } from "../../services/service.api";

export default function ViewNavbar({ sidebarOpen, setSidebarOpen }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userReducer?.user);

  const [ecoles, setEcoles] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [activeAnnee, setActiveAnnee] = useState(null);
  const token = user?.access_token;
  const currentEcoleId = user?.ecole?._id || user?.ecole;

  useEffect(() => {
    // Si l'utilisateur est un ADMIN et qu'il a plusieurs écoles assignées, on récupère les détails
    if (user?.role === "ADMIN" && user?.ecoles && user.ecoles.length > 1) {
      handleGet("/ecole", (res) => {
        if (res?.ecoles) {
          const myAssignedEcoles = res.ecoles.filter(e => user.ecoles.includes(e._id));
          setEcoles(myAssignedEcoles);
        }
      }, token);
    }

    // Fetch all school years to populate dropdown
    if (currentEcoleId && token) {
      handleGet(`/ecole-annee/ecole/${currentEcoleId}`, (res) => {
        if (res && res.annees) {
          setAnnees(res.annees);
          const active = res.annees.find(a => a.is_active);
          if (active) setActiveAnnee(active.annee);
        }
      }, token);
    }
  }, [user?.role, user?.ecoles, currentEcoleId, token]);

  const handleEcoleChange = (e) => {
    const newEcoleId = e.target.value;
    if (!newEcoleId) return;

    // Mettre à jour l'école active dans Redux
    const updatedUser = { ...user, ecole: newEcoleId };
    dispatch({ type: "login", value: { user: updatedUser, token: token } });

    // Optionnel : recharger la page pour forcer le refetching des données de la nouvelle école
    window.location.reload();
  };

  const handleLogout = () => {
    dispatch({ type: "logout" });
    removeAll();
  };

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 md:px-8 z-30 sticky top-0">
      <div className="flex items-center gap-4">
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden p-2 -ml-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Sélecteur d'école pour multi-écoles (Remplace la barre de recherche) */}
        {user?.role === "ADMIN" && ecoles.length > 1 && (
          <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-1.5 w-64">
            <Building2 className="h-4 w-4 text-indigo-600" />
            <select
              value={currentEcoleId}
              onChange={handleEcoleChange}
              className="bg-transparent border-none text-sm font-medium focus:ring-0 cursor-pointer outline-none w-full truncate text-indigo-900"
            >
              {ecoles.map(ec => (
                <option key={ec._id} value={ec._id}>{ec.nom}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 sm:gap-5">


        {/* Sélecteur d'année scolaire */}
        {user?.role === "ADMIN" && <div className="hidden md:flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5 text-amber-800 text-sm font-medium">
          <span>Année Active:</span>
          <select
            value={activeAnnee || ""}
            onChange={async (e) => {
              const selectedAnnee = annees.find(a => a.annee === e.target.value);
              if (selectedAnnee) {
                await handleSetActiveYear(selectedAnnee._id, currentEcoleId, token, () => {
                  setTimeout(() => window.location.reload(), 1000);
                });
              }
            }}
            className="bg-transparent border-none text-sm font-bold focus:ring-0 cursor-pointer outline-none w-28 text-amber-900"
          >
            {annees.map(a => (
              <option key={a._id} value={a.annee}>{a.annee}</option>
            ))}
          </select>
        </div>}

        <div className="h-6 w-px bg-gray-200 hidden sm:block"></div>

        <div className="flex items-center gap-3 group">
          <Link to="/profile" className="hidden sm:flex flex-col items-end hover:bg-gray-50 p-1.5 rounded-lg transition-colors" title="Mon Compte">
            <span className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">{user?.prenom} {user?.nom}</span>
            <span className="text-xs text-indigo-600 font-medium">
              {user?.role === "ADMIN" ? "Administrateur" : user?.role === "SUPER_ADMIN" ? "Super Admin" : user?.role || "Admin"}
            </span>
          </Link>

          <div className="relative cursor-pointer" title="Modifier la photo" onClick={() => document.getElementById("profileUpload").click()}>
            <div className="h-10 w-10 rounded-full bg-indigo-50 border-2 border-indigo-100 flex items-center justify-center p-0.5 hover:ring-2 hover:ring-indigo-300 transition-all overflow-hidden">
              {user?.profil ? (
                <img src={getImageUrl(user.profil)} alt="Profil" className="h-full w-full object-cover rounded-full" />
              ) : (
                <UserCircle className="h-full w-full text-indigo-500" />
              )}
            </div>
            <input
              type="file"
              id="profileUpload"
              className="hidden"
              accept="image/*"
              onChange={(e) => handleUpdateProfilPicture(
                e.target.files[0],
                user._id,
                token,
                (freshUser) => {
                  if (freshUser) dispatch({ type: "user-update", value: { ...user, ...freshUser } });
                }
              )}
            />
          </div>
        </div>

        <button
          onClick={handleLogout}
          title="Déconnexion"
          className="flex items-center justify-center h-9 w-9 rounded-lg bg-gray-50 hover:bg-red-50 text-gray-600 hover:text-red-600 transition-colors border border-gray-200 hover:border-red-200"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}